import { z } from 'zod';

/**
 * The realtime contract, in one place. Adding an event means adding a schema here — call sites
 * never name an event as a bare string (they get the union below), mirroring the typed
 * `EventName` convention in `lib/analytics/events.ts`.
 *
 * Backend-confirmed (ADO 112249, 2026-08-05): the event is `connector-warning` and the payload is
 * `{ serialNumber: number, message: string }`, delivered on the `ws://<API_HOST>:8080/notifications`
 * namespace. The earlier FE-proposed `device.connector_sync_failed` never existed server-side.
 *
 * Still unconfirmed and backend-owned: **delivery targeting.** Story 112249 says "the Admin who
 * configured it" is told, so the event must reach only that admin's session(s) — a per-user room,
 * not a broadcast. Nothing here can enforce that; every subscribed session toasts whatever it is
 * sent.
 *
 * `serialNumber` accepts a string or a number (Digital Matter treats serials as integers, the toast
 * copy needs a string) and nothing else — a `z.coerce.string()` would silently turn `null` into
 * "null" and toast it, instead of reporting the drift.
 *
 * `message` is the server's own wording. It is **optional here and not rendered**: the toast copy is
 * fixed by story 112249's AC and there is no design for a second line, so an omission must degrade to
 * the designed toast rather than fail `parseContract` and suppress the notification entirely. Make it
 * required the day the copy is meant to come from the server.
 */
export const realtimeEventSchemas = {
  'connector-warning': z.object({
    serialNumber: z.union([z.string().min(1), z.number()]).transform(String),
    message: z.string().optional(),
  }),
} satisfies Record<string, z.ZodType>;

export type RealtimeEventName = keyof typeof realtimeEventSchemas;

export type RealtimeEventPayload<E extends RealtimeEventName> = z.infer<
  (typeof realtimeEventSchemas)[E]
>;
